import { useState, useEffect, useCallback, memo } from "react";
import { useNavigate, useParams, Outlet, useLocation, useOutletContext } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Menu, MenuItem } from '@mui/material';
import { MoreVert } from '@mui/icons-material';
import { getAvailableCardTypes, getAvailableCardTypeCategories, getSeriesData } from "../../../../utils/firebase/firebase.utils.js";
import { getCustomCardTypeCategories, updateCustomCardTypeCategory, countCardsInCustomCategory, deleteCustomCardTypeCategory } from "../../../../utils/firebase/checklist.js";
import { getChecklistImageUrl } from "../../../../utils/firebase/storage.js";
import { setChecklistCardTypeDefault, setChecklistCardTypeCategoryDefault } from "../../../../store/system/system.action.js";
import { selectChecklistCardTypeDefault, selectChecklistCardTypeCategoryDefault } from "../../../../store/system/system.selector.js";
import { selectUser, selectRecentlyViewed } from "../../../../store/user/user.selector.js";
import { updateRecentlyViewed } from "../../../../store/user/user.action.js";
import { setSystemNotification } from "../../../../store/system/system.action.js";
import { buildRecentChecklists } from "../../../../utils/checklists/recentChecklists.js";
import { updateUserDocument } from "../../../../utils/firebase/user.js";
import { getUserCache, saveUserCache } from "../../../../utils/cache/userCache.js";

// styles
import {
    ListDisplayContainer,
    ChecklistTitle,
    ChecklistImage,
    FiltersContainer,
    FilterContainer,
    CardTypeBoxesContainer,
    CardTypeBox,
    CardTypeBoxCount,
    CardTypeBoxContent,
    CategoryOptionsIcon,
    HeaderContent,
    LeftContent,
    ChecklistAttributes,
    RightContent,
    AddCustomCardTypeBox,
    ChecklistSeriesInfoContainer,
} from "./ChecklistHeader.styles.js";

// assets
import cardIcon from "../../../../assets/system/icons/card-icon.png"

// components
import LoadingIcon from "../../../../components/loading-icon/LoadingIcon.js";
import ParallelAttribute from "../checklist-display/components/checklist-item-panel/components/parallel-attribute/ParallelAttribute.js";
import CreateCategoryModalContainer from "./components/create-card-type-category-modal/CreateCategoryModalContainer.js";
import RenameCardTypeCategoryModal from "./components/rename-card-type-category-modal/RenameCardTypeCategoryModal.js";
import DeleteCardTypeCategoryModal from "./components/delete-card-type-category-modal/DeleteCardTypeCategoryModal.js";
import TertiaryBtn from "../../../../components/btns/tertiary-btn/TertiaryBtn.js";

// Helpers (module-level to avoid re-creation, used by memoized components)
const formatNumberWithCommas = (number) =>
    number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

const formatReleaseDate = (timestamp) => {
    if (!timestamp) return '';
    let date;
    if (typeof timestamp.toDate === 'function') {
        date = timestamp.toDate();
    } else if (timestamp.seconds !== undefined) {
        date = new Date(timestamp.seconds * 1000 + (timestamp.nanoseconds || 0) / 1000000);
    } else {
        return '';
    }
    const month = String(date.getMonth() + 1);
    const day = String(date.getDate()).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${month}/${day}/${year}`;
};

const ListDisplayHeaderImage = memo(function ListDisplayHeaderImage({ sport, year, manufacturer, series }) {
    if (!series || !sport || !year || !manufacturer) return null;
    return (
        <ChecklistImage
            src={getChecklistImageUrl(sport, year, manufacturer, series)}
            alt={`${series} checklist image`}
        />
    );
});

const ListDisplaySeriesInfo = memo(function ListDisplaySeriesInfo({ series, seriesData }) {
    return (
        <ChecklistSeriesInfoContainer>
            <ChecklistTitle>{series || "Checklist"}</ChecklistTitle>
            {seriesData && (
                <ChecklistAttributes>
                    {seriesData.releaseDate && (
                        <ParallelAttribute color="#FFFFFF" text={`Release Date: ${formatReleaseDate(seriesData.releaseDate)}`} isAlwaysExpanded={true} />
                    )}
                </ChecklistAttributes>
            )}
        </ChecklistSeriesInfoContainer>
    );
});

export default function ChecklistHeader({ viewingUserID = null }) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    // Get context from parent Outlet (e.g., from FriendCollectionPage) and pass it through
    const outletContext = useOutletContext();
    // Use viewingUserID from prop first, then from context, then from outletContext
    const effectiveViewingUserID = viewingUserID || outletContext?.viewingUserID || null;
    const { sport, year, manufacturer, series } = useParams();
    const [cardTypes, setCardTypes] = useState([]);
    const [cardTypeCategories, setCardTypeCategories] = useState([]);
    const [isLoadingCardTypes, setIsLoadingCardTypes] = useState(true);
    const [seriesData, setSeriesData] = useState(null);
    const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [deleteCardCount, setDeleteCardCount] = useState(0);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const checklistCardTypeDefault = useSelector(selectChecklistCardTypeDefault);
    const checklistCardTypeCategoryDefault = useSelector(selectChecklistCardTypeCategoryDefault);
    const user = useSelector(selectUser);
    const recentlyViewed = useSelector(selectRecentlyViewed);

    // Use viewingUserID if provided, otherwise use current user's ID
    const targetUserID = viewingUserID || user?.uid;

    // Helper function to determine base path for navigation
    const getBasePath = useCallback(() => {
        const pathname = location.pathname;

        // Check if we're in a friend collection route first
        if (pathname.includes('/friends/') && pathname.includes('/collection')) {
            const friendMatch = pathname.match(/\/friends\/([^/]+)\/collection/);
            if (friendMatch && friendMatch[1]) {
                return `/friends/${friendMatch[1]}/collection`;
            }
        }

        return '/checklists';
    }, [location.pathname]);

    useEffect(() => {
        if (effectiveViewingUserID || !user?.uid) return;
        if (!location.pathname.startsWith('/checklists/')) return;
        if (!sport || !year || !manufacturer || !series) return;

        const { list, changed } = buildRecentChecklists(recentlyViewed, {
            sport,
            year,
            manufacturer,
            series,
        });
        if (!changed) return;

        dispatch(updateRecentlyViewed(list));
        const cachedUser = getUserCache(user.uid);
        if (cachedUser) {
            saveUserCache({ ...cachedUser, recentChecklists: list }, user.uid);
        }
        updateUserDocument(user.uid, { recentChecklists: list }).catch((error) => {
            console.error('Error saving recent checklist:', error);
        });
    }, [dispatch, effectiveViewingUserID, location.pathname, manufacturer, recentlyViewed, series, sport, user, year]);

    // Fetch available card types and merge with user's collected counts
    useEffect(() => {
        const fetchCardTypes = async () => {
            if (sport && year && manufacturer && series) {
                // Only show full-screen loading on initial load (no card types yet).
                // When user changes card type, we already have card types - keep header visible.
                if (cardTypes.length === 0) {
                    setIsLoadingCardTypes(true);
                }

                // Fetch checklist card types (for card type names)
                const checklistCardTypes = await getAvailableCardTypes(null, sport, year, manufacturer, series);

                // Fetch user's collected card types if logged in
                let collectedCardTypes = [];
                if (targetUserID) {
                    try {
                        collectedCardTypes = await getAvailableCardTypes(targetUserID, sport, year, manufacturer, series);
                    } catch (error) {
                        // Error fetching collected card types
                    }
                }

                // Create a map of collected counts by card type
                const collectedCountsMap = {};
                collectedCardTypes.forEach(ct => {
                    collectedCountsMap[ct.cardType] = ct.totalCardCount || 0;
                });

                // Merge checklist card types with collected counts
                const mergedCardTypes = checklistCardTypes.map(ct => ({
                    cardType: ct.cardType,
                    totalCardCount: collectedCountsMap[ct.cardType] || 0 // Use collected count, default to 0
                }));

                // When viewing a friend's collection, filter to only show card types with at least 1 card
                let filteredCardTypes = mergedCardTypes;
                if (viewingUserID) {
                    filteredCardTypes = mergedCardTypes.filter(ct => ct.totalCardCount > 0);
                }

                // Sort card types alphabetically
                const sortedCardTypes = filteredCardTypes.sort((a, b) =>
                    a.cardType.localeCompare(b.cardType)
                );
                setCardTypes(sortedCardTypes);

                // Auto-select "Base" card type if it exists, otherwise select first one
                if (sortedCardTypes.length > 0 && !checklistCardTypeDefault) {
                    const baseCardType = sortedCardTypes.find(ct => ct.cardType === "Base");
                    const cardTypeToSelect = baseCardType ? baseCardType.cardType : sortedCardTypes[0].cardType;
                    dispatch(setChecklistCardTypeDefault(cardTypeToSelect));
                }

                setIsLoadingCardTypes(false);
            }
        };
        fetchCardTypes();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- cardTypes.length intentionally omitted to avoid re-fetch when we already have data
    }, [sport, year, manufacturer, series, checklistCardTypeDefault, dispatch, targetUserID, viewingUserID]);

    // Fetch available card type categories and merge with user's collected counts
    useEffect(() => {
        const fetchCardTypeCategories = async () => {
            if (sport && year && manufacturer && series && checklistCardTypeDefault) {
                // Fetch checklist categories (for category names)
                const checklistCategories = await getAvailableCardTypeCategories(null, sport, year, manufacturer, series, checklistCardTypeDefault);

                // Fetch user's collected categories if logged in
                let collectedCategories = [];
                if (targetUserID) {
                    try {
                        collectedCategories = await getAvailableCardTypeCategories(targetUserID, sport, year, manufacturer, series, checklistCardTypeDefault);
                    } catch (error) {
                        // Error fetching collected card type categories
                    }
                }

                // Fetch custom categories if logged in (only for current user, not friends)
                let customCategories = [];
                if (user?.uid && !viewingUserID) {
                    try {
                        customCategories = await getCustomCardTypeCategories(user.uid, sport, year, manufacturer, series, checklistCardTypeDefault);
                    } catch (error) {
                        // Error fetching custom card type categories
                    }
                }

                // Create a map of collected counts by category name
                const collectedCountsMap = {};
                if (collectedCategories && typeof collectedCategories === 'object') {
                    Object.entries(collectedCategories).forEach(([key, value]) => {
                        collectedCountsMap[key] = value.cardCount || 0;
                    });
                }

                // Convert checklist categories to array and merge with collected counts
                const officialCategoriesArray = checklistCategories ?
                    Object.entries(checklistCategories)
                        .map(([key, value]) => ({
                            name: key,
                            ...value,
                            cardCount: collectedCountsMap[key] || 0, // Use collected count, default to 0
                            isCustom: false
                        }))
                        .sort((a, b) => a.name.localeCompare(b.name)) : [];

                // When viewing a friend's collection, filter to only show categories with at least 1 card
                let filteredOfficialCategories = officialCategoriesArray;
                if (viewingUserID) {
                    filteredOfficialCategories = officialCategoriesArray.filter(cat => cat.cardCount > 0);
                }

                // Add custom categories with special identifier for navigation
                const customCategoriesArray = customCategories.map(cat => ({
                    ...cat,
                    // Use "custom:" prefix with ID for navigation
                    navigationName: `custom:${cat.id}`
                }));

                // Merge official and custom categories, sort by name
                const allCategories = [...filteredOfficialCategories, ...customCategoriesArray].sort((a, b) => {
                    // Custom categories come after official ones
                    if (a.isCustom !== b.isCustom) {
                        return a.isCustom ? 1 : -1;
                    }
                    return a.name.localeCompare(b.name);
                });

                setCardTypeCategories(allCategories);

                // Auto-select first category if none is selected and categories exist
                if (allCategories.length > 0 && !checklistCardTypeCategoryDefault) {
                    const firstCategory = allCategories[0].isCustom ? allCategories[0].navigationName : allCategories[0].name;
                    dispatch(setChecklistCardTypeCategoryDefault(firstCategory));

                    // Navigate to the selected category
                    const basePath = getBasePath();
                    navigate(`${basePath}/${sport}/${year}/${manufacturer}/${series}/${checklistCardTypeDefault}/${firstCategory}`);
                }

            } else {
                // Clear categories if no card type is selected
                setCardTypeCategories([]);
            }
        };
        fetchCardTypeCategories();
    }, [sport, year, manufacturer, series, checklistCardTypeDefault, checklistCardTypeCategoryDefault, dispatch, navigate, targetUserID, user, viewingUserID, getBasePath]);

    const handleCardTypeChange = (newCardType) => {
        if (newCardType !== checklistCardTypeDefault) {
            dispatch(setChecklistCardTypeDefault(newCardType));
            dispatch(setChecklistCardTypeCategoryDefault(null)); // Reset card type category when card type changes
        }
    };

    const handleCardTypeCategoryChange = (category) => {
        // Handle both official categories (string name) and custom categories (object with navigationName)
        const categoryName = typeof category === 'string' ? category : category.navigationName || category.name;
        dispatch(setChecklistCardTypeCategoryDefault(categoryName));

        const basePath = getBasePath();
        navigate(`${basePath}/${sport}/${year}/${manufacturer}/${series}/${checklistCardTypeDefault}/${categoryName}`);
    };

    const handleMenuOpen = (event, category) => {
        event.stopPropagation(); // Prevent triggering the category selection
        setMenuAnchorEl(event.currentTarget);
        setSelectedCategory(category);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
        setSelectedCategory(null);
    };

    const handleRename = () => {
        // Close menu but keep selectedCategory for the modal
        setMenuAnchorEl(null);
        setIsRenameModalOpen(true);
    };

    const handleRenameSave = async (category, newName) => {
        if (!user?.uid || !category?.id || viewingUserID) {
            throw new Error('Cannot edit categories when viewing a friend\'s collection');
        }

        try {
            await updateCustomCardTypeCategory(
                user.uid,
                sport,
                year,
                manufacturer,
                series,
                checklistCardTypeDefault,
                category.id,
                newName
            );

            // Refresh categories after successful rename
            if (sport && year && manufacturer && series && checklistCardTypeDefault) {
                const customCategories = await getCustomCardTypeCategories(targetUserID, sport, year, manufacturer, series, checklistCardTypeDefault);
                const checklistCategories = await getAvailableCardTypeCategories(null, sport, year, manufacturer, series, checklistCardTypeDefault);

                let collectedCategories = [];
                if (targetUserID) {
                    try {
                        collectedCategories = await getAvailableCardTypeCategories(targetUserID, sport, year, manufacturer, series, checklistCardTypeDefault);
                    } catch (error) {
                        // Error fetching collected card type categories
                    }
                }

                const collectedCountsMap = {};
                if (collectedCategories && typeof collectedCategories === 'object') {
                    Object.entries(collectedCategories).forEach(([key, value]) => {
                        collectedCountsMap[key] = value.cardCount || 0;
                    });
                }

                const officialCategoriesArray = checklistCategories ?
                    Object.entries(checklistCategories)
                        .map(([key, value]) => ({
                            name: key,
                            ...value,
                            cardCount: collectedCountsMap[key] || 0,
                            isCustom: false
                        }))
                        .sort((a, b) => a.name.localeCompare(b.name)) : [];

                const customCategoriesArray = customCategories.map(cat => ({
                    ...cat,
                    navigationName: `custom:${cat.id}`
                }));

                const allCategories = [...officialCategoriesArray, ...customCategoriesArray].sort((a, b) => {
                    if (a.isCustom !== b.isCustom) {
                        return a.isCustom ? 1 : -1;
                    }
                    return a.name.localeCompare(b.name);
                });

                setCardTypeCategories(allCategories);

                // Update navigation if the renamed category is currently selected
                if (checklistCardTypeCategoryDefault === `custom:${category.id}`) {
                    const updatedCategory = customCategories.find(cat => cat.id === category.id);
                    if (updatedCategory) {
                        // The navigation name stays the same (custom:categoryId), so no need to navigate
                        // Just update the display name in state
                    }
                }
            }
        } catch (error) {
            // Error renaming category
            throw error;
        }
    };

    const handleDelete = async () => {
        if (!user?.uid || !selectedCategory?.id || viewingUserID) {
            handleMenuClose();
            return;
        }

        // Close menu but keep selectedCategory for the modal
        setMenuAnchorEl(null);

        try {
            // Count cards in the category
            const cardCount = await countCardsInCustomCategory(
                user.uid,
                sport,
                year,
                manufacturer,
                series,
                checklistCardTypeDefault,
                selectedCategory.id
            );

            if (cardCount > 0) {
                // Show confirmation modal if there are cards
                setDeleteCardCount(cardCount);
                setIsDeleteModalOpen(true);
            } else {
                // Delete immediately if no cards
                await handleConfirmDelete(selectedCategory);
            }
        } catch (error) {
            // Error checking card count
            handleMenuClose();
        }
    };

    const handleConfirmDelete = async (category) => {
        if (!user?.uid || !category?.id || viewingUserID) {
            return;
        }

        try {
            await deleteCustomCardTypeCategory(
                user.uid,
                sport,
                year,
                manufacturer,
                series,
                checklistCardTypeDefault,
                category.id
            );

            // Refresh categories after successful delete
            if (sport && year && manufacturer && series && checklistCardTypeDefault) {
                const customCategories = await getCustomCardTypeCategories(targetUserID, sport, year, manufacturer, series, checklistCardTypeDefault);
                const checklistCategories = await getAvailableCardTypeCategories(null, sport, year, manufacturer, series, checklistCardTypeDefault);

                let collectedCategories = [];
                if (targetUserID) {
                    try {
                        collectedCategories = await getAvailableCardTypeCategories(targetUserID, sport, year, manufacturer, series, checklistCardTypeDefault);
                    } catch (error) {
                        // Error fetching collected card type categories
                    }
                }

                const collectedCountsMap = {};
                if (collectedCategories && typeof collectedCategories === 'object') {
                    Object.entries(collectedCategories).forEach(([key, value]) => {
                        collectedCountsMap[key] = value.cardCount || 0;
                    });
                }

                const officialCategoriesArray = checklistCategories ?
                    Object.entries(checklistCategories)
                        .map(([key, value]) => ({
                            name: key,
                            ...value,
                            cardCount: collectedCountsMap[key] || 0,
                            isCustom: false
                        }))
                        .sort((a, b) => a.name.localeCompare(b.name)) : [];

                const customCategoriesArray = customCategories.map(cat => ({
                    ...cat,
                    navigationName: `custom:${cat.id}`
                }));

                const allCategories = [...officialCategoriesArray, ...customCategoriesArray].sort((a, b) => {
                    if (a.isCustom !== b.isCustom) {
                        return a.isCustom ? 1 : -1;
                    }
                    return a.name.localeCompare(b.name);
                });

                setCardTypeCategories(allCategories);

                // If the deleted category was selected, navigate to first available category or clear selection
                if (checklistCardTypeCategoryDefault === `custom:${category.id}`) {
                    if (allCategories.length > 0) {
                        const firstCategory = allCategories[0].isCustom ? allCategories[0].navigationName : allCategories[0].name;
                        dispatch(setChecklistCardTypeCategoryDefault(firstCategory));
                        const basePath = getBasePath();
                        navigate(`${basePath}/${sport}/${year}/${manufacturer}/${series}/${checklistCardTypeDefault}/${firstCategory}`);
                    } else {
                        dispatch(setChecklistCardTypeCategoryDefault(null));
                    }
                }
            }

            setIsDeleteModalOpen(false);
            setSelectedCategory(null);
        } catch (error) {
            // Error deleting category
            setIsDeleteModalOpen(false);
            setSelectedCategory(null);
        }
    };

    const handleCategoryCreated = (createdCategory) => {
        const newCategory = {
            ...createdCategory,
            navigationName: `custom:${createdCategory.id}`,
        };
        setCardTypeCategories((prev) => {
            const officials = prev.filter((c) => !c.isCustom);
            const custom = [...prev.filter((c) => c.isCustom), newCategory].sort((a, b) =>
                a.name.localeCompare(b.name)
            );
            return [...officials, ...custom];
        });
        dispatch(setChecklistCardTypeCategoryDefault(newCategory.navigationName));
        const basePath = getBasePath();
        navigate(`${basePath}/${sport}/${year}/${manufacturer}/${series}/${checklistCardTypeDefault}/${newCategory.navigationName}`);
    };

    // Fetch series data for card count and release date
    useEffect(() => {
        const fetchSeriesData = async () => {
            if (sport && year && manufacturer && series) {
                try {
                    const data = await getSeriesData(sport, year, manufacturer, series);
                    setSeriesData(data);
                } catch (error) {
                    dispatch(setSystemNotification(`Error fetching series data: ${error.message}`));
                    setSeriesData(null);
                }
            } else {
                setSeriesData(null);
            }
        };
        fetchSeriesData();
    }, [sport, year, manufacturer, series, dispatch]);

    // Full-screen loading only for initial load (card types).
    // Category loading keeps header visible to avoid image flash when switching card types.
    const isInitialLoading = isLoadingCardTypes;
    const checklistsPath = `${getBasePath()}/${sport}/${year}/${manufacturer}`;

    if (isInitialLoading) {
        return (
            <ListDisplayContainer>
                <TertiaryBtn title="CHECKLISTS" backIcon route={checklistsPath} />
                <div style={{ zIndex: 1000 }}>
                    <LoadingIcon />
                </div>
            </ListDisplayContainer>
        );
    }

    return (
        <ListDisplayContainer>
            <TertiaryBtn title="CHECKLISTS" backIcon route={checklistsPath} />
            <HeaderContent>
                <LeftContent>
                    <ListDisplayHeaderImage sport={sport} year={year} manufacturer={manufacturer} series={series} />
                </LeftContent>
                <RightContent>
                    <ListDisplaySeriesInfo series={series} seriesData={seriesData} />
                    <FiltersContainer>
                        {cardTypes.length > 0 && (
                            <FilterContainer>
                                <CardTypeBoxesContainer>
                                    {cardTypes.map((cardType, index) => (
                                        <CardTypeBox
                                            key={index}
                                            $isSelected={checklistCardTypeDefault === cardType.cardType}
                                            onClick={() => handleCardTypeChange(cardType.cardType)}
                                        >
                                            <h4>{cardType.cardType}</h4>
                                            {cardType.totalCardCount !== 0 && (
                                                <CardTypeBoxCount>
                                                    <h5>{formatNumberWithCommas(cardType.totalCardCount)}</h5>
                                                    <img src={cardIcon} alt="card icon" />
                                                </CardTypeBoxCount>
                                            )}
                                        </CardTypeBox>
                                    ))}
                                </CardTypeBoxesContainer>
                            </FilterContainer>
                        )}
                        {checklistCardTypeDefault && cardTypes.length > 0 && (
                            <FilterContainer>
                                <CardTypeBoxesContainer>
                                    <CreateCategoryModalContainer
                                        user={user}
                                        sport={sport}
                                        year={year}
                                        manufacturer={manufacturer}
                                        series={series}
                                        cardType={checklistCardTypeDefault}
                                        onCategoryCreated={handleCategoryCreated}
                                    >
                                        {(openModal) => (
                                            <AddCustomCardTypeBox
                                                $isSelected={false}
                                                onClick={openModal}
                                            >
                                                <h4>+ ADD CUSTOM</h4>
                                            </AddCustomCardTypeBox>
                                        )}
                                    </CreateCategoryModalContainer>
                                    {cardTypeCategories.length > 0 ? (
                                        cardTypeCategories.map((category, index) => {
                                            const categoryName = category.isCustom ? category.navigationName : category.name;
                                            const displayName = category.name;
                                            const isSelected = checklistCardTypeCategoryDefault === categoryName;

                                            return (
                                                <CardTypeBox
                                                    key={category.isCustom ? category.id : index}
                                                    $isSelected={isSelected}
                                                    onClick={() => handleCardTypeCategoryChange(category)}
                                                >
                                                    <CardTypeBoxContent>
                                                        <h4>{displayName}</h4>
                                                        {category.cardCount !== 0 && (
                                                            <CardTypeBoxCount>
                                                                <h5>{formatNumberWithCommas(category.cardCount)}</h5>
                                                                <img src={cardIcon} alt="card icon" />
                                                            </CardTypeBoxCount>
                                                        )}
                                                    </CardTypeBoxContent>
                                                    {category.isCustom && (
                                                        <CategoryOptionsIcon
                                                            onClick={(e) => handleMenuOpen(e, category)}
                                                        >
                                                            <MoreVert />
                                                        </CategoryOptionsIcon>
                                                    )}
                                                </CardTypeBox>
                                            );
                                        })
                                    ) : (
                                        <LoadingIcon />
                                    )}
                                </CardTypeBoxesContainer>
                            </FilterContainer>
                        )}
                    </FiltersContainer>
                </RightContent>
            </HeaderContent>
            <Outlet context={outletContext || (effectiveViewingUserID ? { viewingUserID: effectiveViewingUserID } : undefined)} />
            <RenameCardTypeCategoryModal
                isOpen={isRenameModalOpen}
                onClose={() => {
                    setIsRenameModalOpen(false);
                    setSelectedCategory(null); // Clear selected category when modal closes
                }}
                category={selectedCategory}
                onRename={handleRenameSave}
            />
            <DeleteCardTypeCategoryModal
                isOpen={isDeleteModalOpen}
                onClose={() => {
                    setIsDeleteModalOpen(false);
                    setSelectedCategory(null);
                }}
                category={selectedCategory}
                cardCount={deleteCardCount}
                onConfirmDelete={handleConfirmDelete}
            />
            <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                PaperProps={{
                    style: {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '8px',
                        minWidth: '120px',
                    }
                }}
            >
                <MenuItem
                    onClick={handleRename}
                    sx={{
                        color: '#FFFFFF',
                        fontSize: '14px',
                        '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        }
                    }}
                >
                    RENAME
                </MenuItem>
                <MenuItem
                    onClick={handleDelete}
                    sx={{
                        color: '#FFFFFF',
                        fontSize: '14px',
                        '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        }
                    }}
                >
                    DELETE
                </MenuItem>
            </Menu>
        </ListDisplayContainer>
    )
}
